import React from 'react';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Paper, TableContainer, Button, Grid, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { getScheduledCommandsList } from 'ScheduledCommand/Domain/Services/GetScheduledCommandsListService';
import { Link as RouterLink } from 'react-router-dom';
import { Delete, Edit, PlayArrow } from '@mui/icons-material';
import { deleteScheduledCommand } from 'ScheduledCommand/Domain/Services/DeleteScheduledCommandService';
import { runScheduledCommand } from 'ScheduledCommand/Domain/Services/RunScheduledCommandService';
import { enableScheduledCommand } from 'ScheduledCommand/Domain/Services/EnableScheduledCommandService';
import { disableScheduledCommand } from 'ScheduledCommand/Domain/Services/DisableScheduledCommandService';
import Loader from 'UI/Application/Components/Loaders/Loader';
import DialogLoader from 'UI/Application/Components/Loaders/DialogLoader';
import { PRIVATE_ADMIN_FORM_SCHEDULED_COMMAND } from '../Constants/PathsConstant';
import { useTranslation } from 'react-i18next';

function TableScheduledCommandView() {
  const { t } = useTranslation();
  const [sendingApiRequest, setSendingApiRequest] = React.useState(false);
  const [scheduledCommands, setScheduledCommands] = React.useState([]);
  const [scheduledCommandsRows, setScheduledCommandsRows] = React.useState([]);
  const [loadScheduledCommands, setLoadScheduledCommands] = React.useState(true);
  const [selectedId, setSelectedId] = React.useState(null);


  // Botón eliminar
  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const handleConfirmDelete = () => {
    if (sendingApiRequest) return;
    setSendingApiRequest(true);
    deleteScheduledCommand(selectedId).then(() => {
      setOpenDeleteModal(false);
      setLoadScheduledCommands(true);
      setSelectedId(null);
    }).finally(() => setSendingApiRequest(false));
  };
  const handleDelete = (scheduledcommandId) => {
    setSelectedId(scheduledcommandId);
    setOpenDeleteModal(true);
  };
  // Fin botón eliminar

  // Botón ejecutar
  const [openRunModal, setOpenRunModal] = React.useState(false);
  const handleConfirmRun = () => {
    if (sendingApiRequest) return;
    setSendingApiRequest(true);
    runScheduledCommand(selectedId).then(() => {
      setOpenRunModal(false);
      setLoadScheduledCommands(true);
      setSelectedId(null);
    }).finally(() => setSendingApiRequest(false));
  };
  const handleRun = (scheduledcommandId) => {
    setSelectedId(scheduledcommandId);
    setOpenRunModal(true);
  };
  // Fin botón ejecutar

  // Botón habilitar
  const [openEnableModal, setOpenEnableModal] = React.useState(false);
  const handleConfirmEnable = () => {
    if (sendingApiRequest) return;
    setSendingApiRequest(true);
    enableScheduledCommand(selectedId).then(() => {
      setOpenEnableModal(false);
      setLoadScheduledCommands(true);
      setSelectedId(null);
    }).finally(() => setSendingApiRequest(false));
  };
  const handleEnable = (scheduledcommandId) => {
    setSelectedId(scheduledcommandId);
    setOpenEnableModal(true);
  };
  // Fin botón habilitar

  // Botón deshabilitar
  const [openDisableModal, setOpenDisableModal] = React.useState(false);
  const handleConfirmDisable = () => {
    if (sendingApiRequest) return;
    disableScheduledCommand(selectedId).then(() => {
      setOpenDisableModal(false);
      setLoadScheduledCommands(true);
      setSelectedId(null);
    }).finally(() => setSendingApiRequest(false));
  };
  const handleDisable = (scheduledcommandId) => {
    setSelectedId(scheduledcommandId);
    setOpenDisableModal(true);
  };
  // Fin botón deshabilitar

  // Mostrar último output
  const [openLastOutputModal, setOpenLastOutputModal] = React.useState(false);
  const [showLastOutput, setShowLastOutput] = React.useState('');
  const handleShowLastOutput = (output) => {
    setShowLastOutput(output);
    setOpenLastOutputModal(true);
  };
  // Fin mostrar último output


  // Fetch scheduledcommand data on mount or when loadScheduledCommands flag changes
  React.useEffect(() => {
    if (loadScheduledCommands) {
      getScheduledCommandsList({}).then((response) => {
        setScheduledCommands(response.scheduledCommands);
        setLoadScheduledCommands(false);
      });
    }
  }, [loadScheduledCommands]);

  // Update table rows based on scheduledCommands
  React.useEffect(() => {
    const rows = scheduledCommands.map((scheduledcommand) => (
      <TableRow key={scheduledcommand.id}>
        <TableCell align='center'>{scheduledcommand.description}</TableCell>
        <TableCell align='center'>{scheduledcommand.command}</TableCell>
        <TableCell align='center'>{scheduledcommand.arguments}</TableCell>
        <TableCell align='center'>{scheduledcommand.cronExpression}</TableCell>
        <TableCell align='center'>{scheduledcommand.lastExecution}</TableCell>
        <TableCell align='center'>{scheduledcommand.lastReturnCode === 0 ? 'Éxito' : scheduledcommand.lastReturnCode === 1 ? 'Error' : 'No ejecutado'}</TableCell>
        <TableCell align='center'>{scheduledcommand.lastOutput ? (
          <Button variant="contained" color="primary" onClick={() => handleShowLastOutput(scheduledcommand.lastOutput)}>{t('scheduledcommand.table.lastOutput.show')}</Button>
        ) : 'Sin output'}</TableCell>
        <TableCell align='center'>{scheduledcommand.disabled ? t('scheduledcommand.table.disabled') : t('scheduledcommand.table.enabled')}</TableCell>
        <TableCell align='center'>{scheduledcommand.running ? t('scheduledcommand.table.running') : t('scheduledcommand.table.notRunning')}</TableCell>
        <TableCell align="center">
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <Button variant="contained" color="primary" component={RouterLink} to={`${PRIVATE_ADMIN_FORM_SCHEDULED_COMMAND}/${scheduledcommand.id}`}>
            <Edit />
          </Button>
          <Button variant="contained" color="success" onClick={() => handleRun(scheduledcommand.id)}>
            <PlayArrow />
          </Button>
          {scheduledcommand.disabled ?
            <Button variant="contained" color="success" onClick={() => handleEnable(scheduledcommand.id)}>
              {t('scheduledcommand.table.enable')}
            </Button>
            :
            <Button variant="contained" color="error" onClick={() => handleDisable(scheduledcommand.id)}>
              {t('scheduledcommand.table.disable')}
            </Button>
          }
          <Button variant="contained" color="error" onClick={() => handleDelete(scheduledcommand.id)}>
            <Delete />
          </Button>
          </Box>
        </TableCell>
      </TableRow>
    ));
    setScheduledCommandsRows(rows);
  }, [scheduledCommands]);

  return (
    <>
      <Paper
          sx={{
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          with: "100%"
          }}
      >
          <Box sx={{ p: 2, flex: 1 }}>
              <Box sx={{ marginBottom: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                  </Grid>
                </Grid>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" component={RouterLink} to={PRIVATE_ADMIN_FORM_SCHEDULED_COMMAND + '/new'}>{t('scheduledcommand.table.button.create')}</Button>
              </Box>
              <Typography variant="h6" sx={{ mb: 2, textAlign: "center"}}>{t('scheduledcommand.table.title')}</Typography>
              <TableContainer component={Paper}>
              <Table sx={{ minWidth: "100%" }} aria-label="channels table">
                  <TableHead>
                  <TableRow>
                      <TableCell align='center'>{t('scheduledcommand.table.description')}</TableCell>
                      <TableCell align='center'>{t('scheduledcommand.table.command')}</TableCell>
                      <TableCell align='center'>{t('scheduledcommand.table.arguments')}</TableCell>
                      <TableCell align='center'>{t('scheduledcommand.table.cronExpression')}</TableCell>
                      <TableCell align='center'>{t('scheduledcommand.table.lastExecution')}</TableCell>
                      <TableCell align='center'>{t('scheduledcommand.table.lastReturnCode')}</TableCell>
                      <TableCell align='center'>{t('scheduledcommand.table.lastOutput')}</TableCell>
                      <TableCell align='center'>{t('scheduledcommand.table.status')}</TableCell>
                      <TableCell align='center'>{t('scheduledcommand.table.running')}</TableCell>
                      <TableCell align="center">{t('scheduledcommand.table.actions')}</TableCell>
                  </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadScheduledCommands ? (
                      <TableRow>
                        <TableCell colSpan={100} align="center">
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : (
                      scheduledCommandsRows
                    )}
                  </TableBody>
              </Table>
              </TableContainer>

              {/* Delete confirmation modal */}
              <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
                <DialogTitle>{t('scheduledcommand.table.delete.title')}</DialogTitle>
                <DialogContent>
                  <Typography variant="body1">{t('scheduledcommand.table.delete.phrase')}</Typography>
                </DialogContent>
                <DialogActions>
                  <Button color="secondary" onClick={() => setOpenDeleteModal(false)}>{t('scheduledcommand.table.delete.cancel')}</Button>
                  <Button variant="contained" color="error" onClick={handleConfirmDelete}>{t('scheduledcommand.table.delete.delete')}</Button>
                </DialogActions>
              </Dialog>

              {/* Run confirmation modal */}
              <Dialog open={openRunModal} onClose={() => setOpenRunModal(false)}>
                <DialogTitle>{t('scheduledcommand.table.run.title')}</DialogTitle>
                <DialogContent>
                  <Typography variant="body1">{t('scheduledcommand.table.run.phrase')}</Typography>
                </DialogContent>
                <DialogActions>
                  <Button color="secondary" onClick={() => setOpenRunModal(false)}>{t('scheduledcommand.table.run.cancel')}</Button>
                  <Button variant="contained" color="success" onClick={handleConfirmRun}>{t('scheduledcommand.table.run.run')}</Button>
                </DialogActions>
              </Dialog>

              {/* Enable confirmation modal */}
              <Dialog open={openEnableModal} onClose={() => setOpenEnableModal(false)}>
                <DialogTitle>{t('scheduledcommand.table.enable.title')}</DialogTitle>
                <DialogContent>
                  <Typography variant="body1">{t('scheduledcommand.table.enable.phrase')}</Typography>
                </DialogContent>
                <DialogActions>
                  <Button color="secondary" onClick={() => setOpenEnableModal(false)}>{t('scheduledcommand.table.enable.cancel')}</Button>
                  <Button variant="contained" color="success" onClick={handleConfirmEnable}>{t('scheduledcommand.table.enable.enable')}</Button>
                </DialogActions>
              </Dialog>

              {/* Disable confirmation modal */}
              <Dialog open={openDisableModal} onClose={() => setOpenDisableModal(false)}>
                <DialogTitle>{t('scheduledcommand.table.disable.title')}</DialogTitle>
                <DialogContent>
                  <Typography variant="body1">{t('scheduledcommand.table.disable.phrase')}</Typography>
                </DialogContent>
                <DialogActions>
                  <Button color="secondary" onClick={() => setOpenDisableModal(false)}>{t('scheduledcommand.table.disable.cancel')}</Button>
                  <Button variant="contained" color="error" onClick={handleConfirmDisable}>{t('scheduledcommand.table.disable.disable')}</Button>
                </DialogActions>
              </Dialog>

              {/* Show last output modal */}
              <Dialog open={openLastOutputModal} onClose={() => setOpenLastOutputModal(false)}>
                <DialogTitle>{t('scheduledcommand.table.lastOutput.title')}</DialogTitle>
                <DialogContent>
                  {showLastOutput.split("\n").map((line, index, array) => (
                    <React.Fragment key={index}>
                      {line}{index !== array.length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </DialogContent>
                <DialogActions>
                  <Button color="secondary" onClick={() => setOpenLastOutputModal(false)}>{t('scheduledcommand.table.lastOutput.close')}</Button>
                </DialogActions>
              </Dialog>
          </Box>
          <DialogLoader open={sendingApiRequest} />
      </Paper>
    </>
  );
}

export default TableScheduledCommandView;
